import { useMemo } from 'react';

import { Circle, Ellipse, G, Line, Path, Rect, Svg } from '../../svg';
import { useTheme } from '../../theme';
import {
  FLASHCARDS_EMPTY_ARTWORK_SIZE,
  type FlashcardsEmptyArtworkPalette,
  flashcardsEmptyArtworkPalette,
  type FlashcardsEmptyArtworkProps,
} from './FlashcardsEmptyArtwork.art';

/**
 * The composition is 200×150 (4:3) and the box is 5:4, so the view box pads it
 * 5 above and below — the front card's centre (100, 75) sits at the box centre.
 */
const VIEW_BOX = '0 -5 200 160';

/** Card geometry in composition units — the Rive's rest pose. */
const FRONT = { x: 52.5, y: 27.5, size: 95, radius: 9 };
const TILE = { x: 76, y: 51, size: 48, radius: 12.5 };
const SIDE = { size: 89, radius: 8, cx: 79.4, cy: 73, left: -15, right: 12.29 };
const STROKE = 1;

/** A fanned side card with its midline. */
function SideCard({
  cx,
  angle,
  p,
}: {
  cx: number;
  angle: number;
  p: FlashcardsEmptyArtworkPalette;
}) {
  const x = cx - SIDE.size / 2;
  const y = SIDE.cy - SIDE.size / 2;
  return (
    <G transform={`rotate(${angle} ${cx} ${SIDE.cy})`}>
      <Rect
        x={x}
        y={y}
        width={SIDE.size}
        height={SIDE.size}
        rx={SIDE.radius}
        fill={p.card}
        stroke={p.outline}
        strokeWidth={STROKE}
      />
      <Line
        x1={x}
        y1={SIDE.cy}
        x2={x + SIDE.size}
        y2={SIDE.cy}
        stroke={p.outline}
        strokeWidth={STROKE}
      />
    </G>
  );
}

/**
 * The flashcards empty-state hero as a STILL drawing — both platforms, one
 * source: three fanned cards on a floor shadow, the front one with its
 * sparkle tile, at rest. The animated hero is the Rive (`flashcards-empty.riv`,
 * contract `@flashka/shared/rive/flashcards-empty-rive`), drawn by each app's
 * `FlashcardsEmptyRiveArt` over this: it shows until the file draws, and
 * stays wherever the file cannot (a failed load, a Mac where native Rive is
 * off). Colours are the DS roles of `flashcardsEmptyArtworkPalette`.
 */
export function FlashcardsEmptyArtwork({
  width = FLASHCARDS_EMPTY_ARTWORK_SIZE.width,
  height = FLASHCARDS_EMPTY_ARTWORK_SIZE.height,
}: FlashcardsEmptyArtworkProps) {
  const { isDark } = useTheme();
  const p = useMemo(() => flashcardsEmptyArtworkPalette(isDark), [isDark]);
  const mid = FRONT.y + FRONT.size / 2;
  // The sparkle: a four-point star (concave sides, rounded tips), a "+" and a dot.
  const sx = 100;
  const sy = mid;
  const a = 9.2;
  const k = 1.2;
  const star =
    `M${sx} ${sy - a} Q${sx + k} ${sy - k} ${sx + a} ${sy} ` +
    `Q${sx + k} ${sy + k} ${sx} ${sy + a} ` +
    `Q${sx - k} ${sy + k} ${sx - a} ${sy} ` +
    `Q${sx - k} ${sy - k} ${sx} ${sy - a}Z`;
  return (
    <Svg width={width} height={height} viewBox={VIEW_BOX} fill="none" aria-hidden>
      <Ellipse cx={100} cy={127.8} rx={77} ry={15.3} fill={p.shadow} />
      <SideCard cx={SIDE.cx} angle={SIDE.left} p={p} />
      <SideCard cx={200 - SIDE.cx} angle={SIDE.right} p={p} />
      <Rect
        x={FRONT.x}
        y={FRONT.y}
        width={FRONT.size}
        height={FRONT.size}
        rx={FRONT.radius}
        fill={p.card}
        stroke={p.outline}
        strokeWidth={STROKE}
      />
      <Line
        x1={FRONT.x}
        y1={mid}
        x2={FRONT.x + FRONT.size}
        y2={mid}
        stroke={p.outline}
        strokeWidth={STROKE}
      />
      <Rect
        x={TILE.x}
        y={TILE.y}
        width={TILE.size}
        height={TILE.size}
        rx={TILE.radius}
        fill={p.tile}
      />
      <Path
        d={star}
        fill={p.glyph}
        stroke={p.glyph}
        strokeWidth={2.8}
        strokeLinejoin="round"
      />
      <Path
        d={`M${sx + 8} ${sy - 10.1}V${sy - 4.9}M${sx + 5.4} ${sy - 7.5}H${sx + 10.6}`}
        stroke={p.glyph}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
      <Circle cx={sx - 8} cy={sy + 7.1} r={2.4} fill={p.glyph} />
    </Svg>
  );
}
(FlashcardsEmptyArtwork as { displayName?: string }).displayName =
  'FlashcardsEmptyArtwork';
